"use client"

import { useState, useEffect, useCallback, useMemo } from "react"
import { useRouter } from "next/navigation"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { MeetingUserSelector, type MeetingUser } from "@/features/meeting-attendance/components/MeetingUserSelector"
import { MEETING_MANAGEMENT_ROLES } from "@/features/meeting-attendance/constants/roles"
import {
  COMMON_TIMEZONES,
  getUserTimezone,
  TENANT_DEFAULT_TIMEZONE,
  convertFromUTC,
  convertToUTC,
  formatInstantWithTimezone,
} from "@/features/meeting-attendance/utils/timezone"
import { meetingAttendanceApi } from "@/services/api/meeting-attendance"
import CityAutocompleteInput from "@/components/common/CityAutocompleteInput"
import { Switch } from "@/components/ui/switch"
import type { CheckinMode } from "@/features/meeting-attendance/constants/checkin-mode"
import { useAuthGuard } from "@/hooks/useAuthGuard"
import { useAuth } from "@/lib/auth"
import { useTranslation } from "@/hooks/useTranslation"
import { 
  Plus, 
  MapPin, 
  Users, 
  Repeat,
  Edit,
  Trash2,
  Play,
  Pause,
  History,
  X,
  Download,
  FileText,
  Calendar,
  Info,
  Clock,
  UserCog
} from "lucide-react"
import { toast } from "sonner"
import JSZip from "jszip"
import { saveAs } from "file-saver"
import { Document, Packer, Paragraph, ImageRun, AlignmentType, HeadingLevel } from "docx"

interface HistoryMeeting {
  id: string
  title: string
  startTime: string
  endTime: string
  timezone?: string
  stats: {
    attendanceRate: number
    totalRequired: number
    actualAttendees: number
    absentees: number
  }
  attendances: {
    id: string
    status: string
    user: {
      displayName: string
    }
  }[]
}

interface MeetingSeries {
  id: string
  title: string
  description?: string
  pattern: string
  frequency: number
  startDate: string
  endDate?: string
  maxOccurrences?: number
  location?: string
  city?: string | null
  enforceCheckinMode?: boolean
  type: string
  timezone: string
  isActive: boolean
  createdAt: string
  creator: {
    displayName: string
    email: string
  }
  meetings: {
    id: string
    title: string
    startTime: string
    endTime: string
    status: string
    instanceNumber: number
    location?: string
    qrCodeOnline?: string
    qrCodeOffline?: string
  }[]
}

export default function SeriesPage() {
  const router = useRouter()
  const { isReady } = useAuthGuard()
  const { hasAnyRole, isAdmin } = useAuth()
  const { t } = useTranslation()
  const [series, setSeries] = useState<MeetingSeries[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [editingSeries, setEditingSeries] = useState<MeetingSeries | null>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [historyData, setHistoryData] = useState<{ 
    meetings: HistoryMeeting[]; 
    summary?: { 
      totalMeetings: number; 
      averageAttendance: number 
    } 
  } | null>(null)
  const [selectedAttendeesWithRoles, setSelectedAttendeesWithRoles] = useState<{ user: MeetingUser; role: string }[]>([])
  const [showAttendeesModal, setShowAttendeesModal] = useState(false)
  const [currentSeriesId, setCurrentSeriesId] = useState<string | null>(null)
  const [defaultAttendees, setDefaultAttendees] = useState<{ id?: string; role: string; user?: MeetingUser; displayName?: string; email?: string }[]>([])
  const [attendeePrefKeyword, setAttendeePrefKeyword] = useState("")
  const [attendeePrefs, setAttendeePrefs] = useState<Record<string, CheckinMode | null>>({})
  const [selectedForBulk, setSelectedForBulk] = useState<Set<string>>(new Set())
  const [showAddAttendeeModal, setShowAddAttendeeModal] = useState(false)
  const [newAttendeeUsers, setNewAttendeeUsers] = useState<MeetingUser[]>([])
  const [newAttendeeRole, setNewAttendeeRole] = useState("OPTIONAL_ATTENDEE")
  const [showEditRoleModal, setShowEditRoleModal] = useState(false)
  const [editingAttendee, setEditingAttendee] = useState<{ id?: string; displayName?: string; email?: string; role: string } | null>(null)
  const [newEditRole, setNewEditRole] = useState("")
  const [showCreateAttendeeModal, setShowCreateAttendeeModal] = useState(false)
  const [createFormAttendeeUsers, setCreateFormAttendeeUsers] = useState<MeetingUser[]>([])
  const [createFormAttendeeRole, setCreateFormAttendeeRole] = useState("OPTIONAL_ATTENDEE")
  const [showDateRangeModal, setShowDateRangeModal] = useState(false)
  const [dateRangeAction, setDateRangeAction] = useState<'zip' | 'word' | null>(null)
  const [selectedSeriesId, setSelectedSeriesId] = useState<string | null>(null)
  const [showUpdateScheduleModal, setShowUpdateScheduleModal] = useState(false)
  const [scheduleUpdate, setScheduleUpdate] = useState({
    seriesId: "",
    startTime: "",
    duration: 60,
    timezone: TENANT_DEFAULT_TIMEZONE
  })
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: ""
  })
  const [expandedSeries, setExpandedSeries] = useState<Set<string>>(new Set())
  const [qrCodeType, setQrCodeType] = useState<'both' | 'online' | 'onsite'>('both')
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    pattern: "WEEKLY",
    frequency: 1,
    startDateTime: "", // 合并 startDate 和 startTime
    endDate: "",
    maxOccurrences: 0,
    location: "",
    city: "",
    enforceCheckinMode: false,
    type: "OFFLINE",
    duration: 60,
    timezone: TENANT_DEFAULT_TIMEZONE
  })

  const canManage = isAdmin || hasAnyRole(MEETING_MANAGEMENT_ROLES)
  const displayTimezone = useMemo(() => getUserTimezone(), [])
  const seriesTexts = t.meetingAttendance.series
  const commonTexts = t.meetingAttendance.common
  const attendeeTexts = t.meetingAttendance.attendees
  const statusTexts = t.meetingAttendance.status
  const typeTexts = t.meetingAttendance.type

  const attendeeRoleOptions = useMemo(() => ([
    { value: "C_SUITE_ATTENDEE", label: attendeeTexts.roleCsuite },
    { value: "LEVEL_1_REPORT_ATTENDEE", label: attendeeTexts.roleLevel1 },
    { value: "LEVEL_2_REPORT_ATTENDEE", label: attendeeTexts.roleLevel2 },
    { value: "MEETING_OPERATIONS", label: attendeeTexts.roleOperations },
    { value: "REGULAR_ATTENDEE", label: attendeeTexts.roleRegular },
    { value: "OPTIONAL_ATTENDEE", label: attendeeTexts.roleOptional }
  ]), [attendeeTexts])

  const getAttendeeRoleLabel = useCallback((role: string) => {
    const found = attendeeRoleOptions.find(option => option.value === role)
    return found ? found.label : role
  }, [attendeeRoleOptions])

  const formatSeriesInstant = useCallback((value: string | Date, _timezone?: string | null) => {
    const date = value instanceof Date ? value : new Date(value);
    return formatInstantWithTimezone(date, displayTimezone);
  }, [displayTimezone]);

  const fetchSeries = useCallback(async () => {
    try {
      const data = await meetingAttendanceApi.listSeries()
      const normalized = (data.series || []).map((seriesItem: MeetingSeries) => ({
        ...seriesItem,
        meetings: [...(seriesItem.meetings || [])].sort((a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
        ),
      }))
      setSeries(normalized)
    } catch (error) {
      console.error("Failed to fetch series list:", error)
      toast.error(seriesTexts.loadFailed)
    } finally {
      setLoading(false)
    }
  }, [seriesTexts.loadFailed])

  useEffect(() => {
    if (!isReady) {
      return
    }
    fetchSeries()
  }, [fetchSeries, isReady])

  const handleCreateSeries = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      // 从 datetime-local 中提取日期和时间
      const startDateTimeUTC = convertToUTC(formData.startDateTime, formData.timezone).toISOString()
      
      // 如果有 endDate，使用 startDateTime 的时间部分
      const startTime = formData.startDateTime.split('T')[1] || '00:00'
      
      await meetingAttendanceApi.createSeries({
        ...formData,
        city: formData.city || undefined,
        enforceCheckinMode: formData.enforceCheckinMode,
        startDate: startDateTimeUTC,
        endDate: formData.endDate ? convertToUTC(`${formData.endDate}T${startTime}`, formData.timezone).toISOString() : null,
        maxOccurrences: formData.maxOccurrences > 0 ? formData.maxOccurrences : null,
        attendees: selectedAttendeesWithRoles.map(item => ({
          userId: item.user.id,
          role: item.role,
        }))
      })

      toast.success(seriesTexts.createSuccess)
      setShowCreateForm(false)
      resetForm()
      setSelectedAttendeesWithRoles([])
      fetchSeries()
    } catch (error) {
      console.error("Failed to create series:", error)
      toast.error(seriesTexts.createFailed)
    }
  }

  const toggleSeriesStatus = async (seriesId: string, isActive: boolean) => {
    try {
      await meetingAttendanceApi.updateSeries(seriesId, { isActive: !isActive })
      fetchSeries()
    } catch (error) {
      console.error("Failed to toggle series status:", error)
      toast.error(seriesTexts.toggleFailed)
    }
  }

  const deleteSeries = async (seriesId: string) => {
    if (!window.confirm(seriesTexts.deleteConfirm)) {
      return
    }

    const previous = series
    setSeries((prev) => prev.filter((item) => item.id !== seriesId))

    try {
      await meetingAttendanceApi.deleteSeries(seriesId)
      toast.success(seriesTexts.deleteSuccess)
    } catch (error) {
      setSeries(previous)
      console.error("Failed to delete meeting series:", error)
      toast.error(seriesTexts.deleteFailed)
    }
  }

  const startEditSeries = (seriesItem: MeetingSeries) => {
    setEditingSeries(seriesItem)
    // 将UTC时间转换为目标时区显示
    const seriesTimezone = seriesItem.timezone || "UTC"
    const startDateTime = convertFromUTC(new Date(seriesItem.startDate), seriesTimezone)
    setFormData({
      title: seriesItem.title,
      description: seriesItem.description || "",
      pattern: seriesItem.pattern,
      frequency: seriesItem.frequency,
      startDateTime: startDateTime, // 合并的日期时间
      endDate: seriesItem.endDate ? convertFromUTC(new Date(seriesItem.endDate), seriesTimezone).split('T')[0] : "",
      maxOccurrences: seriesItem.maxOccurrences ?? 0,
      location: seriesItem.location || "",
      city: seriesItem.city || "",
      enforceCheckinMode: seriesItem.enforceCheckinMode ?? false,
      type: seriesItem.type,
      duration: 60, // Default value, should be fetched from meeting instance
      timezone: seriesTimezone
    })
    setShowEditForm(true)
  }

  const handleEditSeries = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingSeries) return

    try {
      // 从 datetime-local 中提取日期和时间
      const startDateTimeUTC = convertToUTC(formData.startDateTime, formData.timezone).toISOString()
      const startTime = formData.startDateTime.split('T')[1] || '00:00'
      const endDateUTC = formData.endDate ? convertToUTC(`${formData.endDate}T${startTime}`, formData.timezone).toISOString() : null

      await meetingAttendanceApi.updateSeries(editingSeries.id, {
        title: formData.title,
        description: formData.description,
        location: formData.location,
        city: formData.city || null,
        pattern: formData.pattern,
        frequency: formData.frequency,
        startDate: startDateTimeUTC,
        endDate: endDateUTC,
        maxOccurrences: formData.maxOccurrences > 0 ? formData.maxOccurrences : null,
        type: formData.type,
        timezone: formData.timezone
      })

      if ((formData.enforceCheckinMode ?? false) !== (editingSeries.enforceCheckinMode ?? false)) {
        try {
          await meetingAttendanceApi.updateSeriesEnforceCheckinMode(
            editingSeries.id,
            formData.enforceCheckinMode,
          )
        } catch (err) {
          console.error('Failed to toggle series enforce checkin mode:', err)
          toast.error(t.meetingAttendance.enforceCheckinMode.toggleFailed)
        }
      }

      toast.success(seriesTexts.updateSuccess)
      setShowEditForm(false)
      setEditingSeries(null)
      resetForm()
      fetchSeries()
    } catch (error) {
      console.error("Failed to update meeting series:", error)
      toast.error(seriesTexts.updateFailed)
    }
  }

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      pattern: "WEEKLY",
      frequency: 1,
      startDateTime: "", // 合并后的日期时间
      endDate: "",
      maxOccurrences: 0,
      location: "",
      city: "",
      enforceCheckinMode: false,
      type: "OFFLINE",
      duration: 60,
      timezone: getUserTimezone()
    })
  }

  const viewHistory = async (seriesId: string) => {
    try {
      const data = await meetingAttendanceApi.getSeriesHistory(seriesId)
      setHistoryData(data)
      setShowHistoryModal(true)
    } catch (error) {
      console.error("Failed to fetch history data:", error)
      toast.error(seriesTexts.historyLoadFailed)
    }
  }

  const manageAttendees = async (seriesId: string) => {
    setCurrentSeriesId(seriesId)
    setShowAttendeesModal(true)
    await fetchDefaultAttendees(seriesId)
  }

  const openUpdateScheduleModal = (seriesId: string, currentStartTime: string, seriesTimezone: string) => {
    // 从第一个会议的开始时间提取时间部分
    const firstMeetingStart = new Date(currentStartTime)
    const hours = String(firstMeetingStart.getHours()).padStart(2, '0')
    const minutes = String(firstMeetingStart.getMinutes()).padStart(2, '0')
    
    setScheduleUpdate({
      seriesId,
      startTime: `${hours}:${minutes}`,
      duration: 60, // 默认60分钟
      timezone: seriesTimezone || getUserTimezone()
    })
    setShowUpdateScheduleModal(true)
  }

  const updateSeriesSchedule = async () => {
    if (!scheduleUpdate.seriesId || !scheduleUpdate.startTime || !scheduleUpdate.duration || !scheduleUpdate.timezone) {
      toast.error(seriesTexts.scheduleMissingFields)
      return
    }

    try {
      toast.loading(seriesTexts.scheduleUpdating)
      
      const result = await meetingAttendanceApi.updateSeriesSchedule(scheduleUpdate.seriesId, {
        startTime: scheduleUpdate.startTime,
        duration: scheduleUpdate.duration,
        timezone: scheduleUpdate.timezone
      })

      toast.dismiss()

      toast.success(seriesTexts.scheduleUpdateSuccess.replace('{count}', String(result.updatedCount || 0)))
      setShowUpdateScheduleModal(false)
      fetchSeries() // 重新获取系列会议列表
    } catch (error) {
      toast.dismiss()
      console.error("Failed to update schedules:", error)
      toast.error(seriesTexts.scheduleUpdateFailed)
    }
  }

  const fetchDefaultAttendees = async (seriesId: string) => {
    try {
      const [data, prefsResp] = await Promise.all([
        meetingAttendanceApi.getSeriesAttendees(seriesId),
        meetingAttendanceApi.listSeriesAttendeePreferences(seriesId, { pageSize: 200 }).catch(() => ({ items: [] })),
      ])
      setDefaultAttendees(data.defaultAttendees || [])
      const prefMap: Record<string, CheckinMode | null> = {}
      const prefItems = Array.isArray(prefsResp)
        ? prefsResp
        : Array.isArray((prefsResp as any)?.items)
          ? (prefsResp as any).items
          : Array.isArray((prefsResp as any)?.preferences)
            ? (prefsResp as any).preferences
            : []
      for (const p of prefItems) {
        if (p?.userId && p?.defaultCheckinMode) {
          prefMap[p.userId] = p.defaultCheckinMode
        }
      }
      setAttendeePrefs(prefMap)
    } catch (error) {
      console.error("Failed to fetch attendees data:", error)
      toast.error(seriesTexts.attendeesLoadFailed)
    }
  }

  const handleChangeAttendeePref = async (
    userId: string,
    mode: CheckinMode | null,
  ) => {
    if (!currentSeriesId) return
    try {
      if (mode == null) {
        await meetingAttendanceApi.deleteSeriesAttendeePreference(currentSeriesId, userId)
      } else {
        await meetingAttendanceApi.upsertSeriesAttendeePreferences(currentSeriesId, [
          { userId, defaultCheckinMode: mode },
        ])
      }
      setAttendeePrefs((prev) => ({ ...prev, [userId]: mode }))
      toast.success(seriesTexts.attendeePrefUpdateSuccess)
    } catch (error) {
      console.error('Failed to update series attendee preference:', error)
      toast.error(seriesTexts.attendeePrefUpdateFailed)
    }
  }

  const handleBulkSetAttendeePref = async (mode: CheckinMode | null) => {
    if (!currentSeriesId || selectedForBulk.size === 0) return
    const userIds = Array.from(selectedForBulk)
    try {
      if (mode == null) {
        await Promise.all(
          userIds.map((uid) =>
            meetingAttendanceApi.deleteSeriesAttendeePreference(currentSeriesId, uid),
          ),
        )
      } else {
        await meetingAttendanceApi.upsertSeriesAttendeePreferences(
          currentSeriesId,
          userIds.map((userId) => ({ userId, defaultCheckinMode: mode })),
        )
      }
      setAttendeePrefs((prev) => {
        const next = { ...prev }
        for (const uid of userIds) {
          if (mode == null) delete next[uid]
          else next[uid] = mode
        }
        return next
      })
      setSelectedForBulk(new Set())
      toast.success(
        seriesTexts.attendeeBulkUpdateSuccess.replace('{count}', String(userIds.length)),
      )
    } catch (error) {
      console.error('Failed to bulk update series attendee preferences:', error)
      toast.error(seriesTexts.attendeePrefUpdateFailed)
    }
  }

  const addAttendees = async () => {
    if (!currentSeriesId || newAttendeeUsers.length === 0) return

    try {
      const attendees = newAttendeeUsers.map(user => ({
        userId: user.id,
        role: newAttendeeRole
      }))

      await meetingAttendanceApi.setSeriesAttendees(currentSeriesId, {
        attendees,
        action: 'add'
      })

      toast.success(seriesTexts.attendeesAdded)
      setNewAttendeeUsers([])
      await fetchDefaultAttendees(currentSeriesId)
    } catch (error) {
      console.error("Failed to add attendees:", error)
      toast.error(seriesTexts.attendeesAddFailed)
    }
  }

  const removeAttendee = async (attendeeId: string) => {
    if (!currentSeriesId) return

    try {
      const result = await meetingAttendanceApi.deleteSeriesAttendee(currentSeriesId, { attendeeId })
      const deletedCount = result.deletedCount || 0
      const affectedMeetings = result.affectedMeetings || 0
      toast.success(
        seriesTexts.attendeeRemoved
          .replace('{deletedCount}', String(deletedCount))
          .replace('{affectedMeetings}', String(affectedMeetings))
      )
      await fetchDefaultAttendees(currentSeriesId)
    } catch (error) {
      console.error("Failed to remove attendee:", error)
      toast.error(seriesTexts.attendeeRemoveFailed)
    }
  }

  const openEditRoleModal = (attendee: { id?: string; displayName?: string; email?: string; role: string; user?: MeetingUser }) => {
    setEditingAttendee({
      id: attendee.id,
      role: attendee.role,
      displayName: attendee.displayName || attendee.user?.displayName,
      email: attendee.email || attendee.user?.email,
    })
    setNewEditRole(attendee.role)
    setShowEditRoleModal(true)
  }

  const updateAttendeeRole = async () => {
    if (!currentSeriesId || !editingAttendee?.id || !newEditRole) {
      toast.error(seriesTexts.attendeeRoleMissing)
      return
    }

    try {
      toast.loading(seriesTexts.attendeeRoleUpdating)

      const result = await meetingAttendanceApi.updateSeriesAttendeeRole(currentSeriesId, {
        attendeeId: editingAttendee.id,
        newRole: newEditRole
      })

      toast.dismiss()

      const updatedCount = result.updatedCount || 0
      const affectedMeetings = result.affectedMeetings || 0
      toast.success(
        seriesTexts.attendeeRoleUpdated
          .replace('{updatedCount}', String(updatedCount))
          .replace('{affectedMeetings}', String(affectedMeetings))
      )
      setShowEditRoleModal(false)
      setEditingAttendee(null)
      await fetchDefaultAttendees(currentSeriesId)
    } catch (error) {
      toast.dismiss()
      console.error("Failed to update role:", error)
      toast.error(seriesTexts.attendeeRoleUpdateFailed)
    }
  }

  const getPatternText = (pattern: string, frequency: number) => {
    const patterns = {
      DAILY: seriesTexts.patternDaily,
      WEEKLY: seriesTexts.patternWeekly,
      MONTHLY: seriesTexts.patternMonthly,
      YEARLY: seriesTexts.patternYearly
    }
    const template = patterns[pattern as keyof typeof patterns] || pattern
    return template.replace('{frequency}', String(frequency))
  }

  const getStatusColor = (status: string) => {
    const colors = {
      SCHEDULED: "text-blue-600",
      IN_PROGRESS: "text-green-600",
      COMPLETED: "text-gray-600",
      CANCELLED: "text-red-600"
    }
    return colors[status as keyof typeof colors] || "text-gray-600"
  }

  // 打开日期选择对话框
  const openDateRangeModal = (seriesId: string, action: 'zip' | 'word') => {
    const seriesItem = series.find(s => s.id === seriesId)
    if (!seriesItem || seriesItem.meetings.length === 0) return

    // 自动设置日期范围为第一个和最后一个会议的日期
    const sortedMeetings = [...seriesItem.meetings].sort((a, b) => 
      new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
    )
    
    setDateRange({
      startDate: new Date(sortedMeetings[0].startTime).toISOString().split('T')[0],
      endDate: new Date(sortedMeetings[sortedMeetings.length - 1].startTime).toISOString().split('T')[0]
    })
    
    setQrCodeType('both') // 重置为默认值
    setSelectedSeriesId(seriesId)
    setDateRangeAction(action)
    setShowDateRangeModal(true)
  }

  // 确认下载
  const confirmDownload = async () => {
    if (!selectedSeriesId || !dateRangeAction) return

    setShowDateRangeModal(false)

    if (dateRangeAction === 'zip') {
      await downloadQRCodesAsZip(selectedSeriesId, dateRange.startDate, dateRange.endDate, qrCodeType)
    } else if (dateRangeAction === 'word') {
      await downloadQRCodesAsWord(selectedSeriesId, dateRange.startDate, dateRange.endDate, qrCodeType)
    }

    // 重置状态
    setSelectedSeriesId(null)
    setDateRangeAction(null)
    setDateRange({ startDate: "", endDate: "" })
    setQrCodeType('both')
  }

  // 批量下载二维码到ZIP
  const downloadQRCodesAsZip = async (seriesId: string, startDate?: string, endDate?: string, qrType: 'both' | 'online' | 'onsite' = 'both') => {
    try {
      const seriesItem = series.find(s => s.id === seriesId)
      if (!seriesItem) return

      // 过滤日期范围内的会议
      let filteredMeetings = seriesItem.meetings
      if (startDate || endDate) {
        filteredMeetings = seriesItem.meetings.filter(meeting => {
          const meetingDate = new Date(meeting.startTime)
          const start = startDate ? new Date(startDate) : null
          const end = endDate ? new Date(endDate) : null
          
          if (start && end) {
            return meetingDate >= start && meetingDate <= end
          } else if (start) {
            return meetingDate >= start
          } else if (end) {
            return meetingDate <= end
          }
          return true
        })
      }

      if (filteredMeetings.length === 0) {
        toast.error(seriesTexts.qrRangeEmpty)
        return
      }

      const qrTypeText = qrType === 'both'
        ? seriesTexts.qrTypeAll
        : qrType === 'online'
          ? seriesTexts.qrTypeOnlineOnly
          : seriesTexts.qrTypeOnsiteOnly
      toast.info(seriesTexts.qrPrepare.replace('{type}', qrTypeText).replace('{count}', String(filteredMeetings.length)))

      const zip = new JSZip()
      const folder = zip.folder(seriesItem.title.replace(/[^a-z0-9]/gi, '_'))
      
      if (!folder) return

      for (const meeting of filteredMeetings) {
        const date = new Date(meeting.startTime).toISOString().split('T')[0]
        const meetingName = `${meeting.title.replace(/[^a-z0-9]/gi, '_')}-${date}`

        // 下载 Online QR Code
        if ((qrType === 'both' || qrType === 'online') && meeting.qrCodeOnline) {
          try {
            const response = await fetch(meeting.qrCodeOnline)
            const blob = await response.blob()
            folder.file(`${meetingName}-online.png`, blob)
          } catch (error) {
            console.error(`Failed to download online QR for ${meeting.title}:`, error)
          }
        }

        // 下载 Offline QR Code
        if ((qrType === 'both' || qrType === 'onsite') && meeting.qrCodeOffline) {
          try {
            const response = await fetch(meeting.qrCodeOffline)
            const blob = await response.blob()
            folder.file(`${meetingName}-onsite.png`, blob)
          } catch (error) {
            console.error(`Failed to download offline QR for ${meeting.title}:`, error)
          }
        }
      }

      const zipBlob = await zip.generateAsync({ type: "blob" })
      const suffix = qrType === 'both' ? 'All' : qrType === 'online' ? 'Online' : 'Onsite'
      saveAs(zipBlob, `${seriesItem.title.replace(/[^a-z0-9]/gi, '_')}-${suffix}-QR-Codes.zip`)
      toast.success(seriesTexts.qrDownloadSuccess)
    } catch (error) {
      console.error("Failed to download QR codes:", error)
      toast.error(seriesTexts.qrDownloadFailed)
    }
  }

  // 批量生成Word文档 - 合并到一个文档
  const downloadQRCodesAsWord = async (seriesId: string, startDate?: string, endDate?: string, qrType: 'both' | 'online' | 'onsite' = 'both') => {
    try {
      const seriesItem = series.find(s => s.id === seriesId)
      if (!seriesItem) return

      // 过滤日期范围内的会议
      let filteredMeetings = seriesItem.meetings
      if (startDate || endDate) {
        filteredMeetings = seriesItem.meetings.filter(meeting => {
          const meetingDate = new Date(meeting.startTime)
          const start = startDate ? new Date(startDate) : null
          const end = endDate ? new Date(endDate) : null
          
          if (start && end) {
            return meetingDate >= start && meetingDate <= end
          } else if (start) {
            return meetingDate >= start
          } else if (end) {
            return meetingDate <= end
          }
          return true
        })
      }

      if (filteredMeetings.length === 0) {
        toast.error(seriesTexts.qrRangeEmpty)
        return
      }

      const qrTypeText = qrType === 'both'
        ? ''
        : ` (${qrType === 'online' ? seriesTexts.qrTypeOnlineOnly : seriesTexts.qrTypeOnsiteOnly})`
      toast.info(
        seriesTexts.wordPreparing
          .replace('{count}', String(filteredMeetings.length))
          .replace('{type}', qrTypeText)
      )

      const allSections = []

      // 添加文档标题
      allSections.push(
        new Paragraph({
          text: seriesItem.title,
          heading: HeadingLevel.TITLE,
          alignment: AlignmentType.CENTER,
          spacing: { after: 400 }
        })
      )

      const dateRangeText = startDate || endDate 
        ? ` (${startDate || seriesTexts.qrRangeStartFallback} ${seriesTexts.qrRangeTo} ${endDate || seriesTexts.qrRangeEndFallback})`
        : ''
      
      const qrTypeLabel = qrType === 'online'
        ? ` - ${seriesTexts.qrTypeOnlineOnly}`
        : qrType === 'onsite'
          ? ` - ${seriesTexts.qrTypeOnsiteOnly}`
          : ''
      
      allSections.push(
        new Paragraph({
          text: seriesTexts.wordTitle
            .replace('{count}', String(filteredMeetings.length))
            .replace('{range}', dateRangeText)
            .replace('{type}', qrTypeLabel),
          alignment: AlignmentType.CENTER,
          spacing: { after: 600 }
        })
      )

      // 添加分页符，让标题页单独一页
      allSections.push(
        new Paragraph({
          text: "",
          pageBreakBefore: true
        })
      )

      // 二维码尺寸 - 单一类型时放大
      const qrCodeSize = qrType === 'both' ? 300 : 400

      // 为每个会议添加内容
      for (let i = 0; i < filteredMeetings.length; i++) {
        const meeting = filteredMeetings[i]

        // 如果不是第一个会议，添加分页符
        if (i > 0) {
          allSections.push(
            new Paragraph({
              text: "",
              pageBreakBefore: true
            })
          )
        }

        // 添加会议标题
        allSections.push(
          new Paragraph({
            text: meeting.title,
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { after: 300 }
          })
        )

        // 添加会议信息
        allSections.push(
          new Paragraph({
          text: `${seriesTexts.wordDateLabel}: ${formatSeriesInstant(new Date(meeting.startTime), seriesItem.timezone || "UTC")}`,
          alignment: AlignmentType.CENTER,
          spacing: { after: 200 }
        })
      )

      if (meeting.location) {
        allSections.push(
          new Paragraph({
            text: `${seriesTexts.wordLocationLabel}: ${meeting.location}`,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 }
          })
        )
      }

        // 添加 Online QR Code
        if ((qrType === 'both' || qrType === 'online') && meeting.qrCodeOnline) {
          try {
            console.log(`Fetching online QR code: ${meeting.qrCodeOnline}`)
            const response = await fetch(meeting.qrCodeOnline)
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            
            const arrayBuffer = await response.arrayBuffer()
            const uint8Array = new Uint8Array(arrayBuffer)
            
            console.log(`Successfully loaded online QR code, size: ${uint8Array.length} bytes`)
            
            allSections.push(
              new Paragraph({
                text: seriesTexts.qrOnlineLabel,
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                spacing: { before: 400, after: 200 }
              })
            )

            allSections.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    type: "png",
                    data: uint8Array,
                    transformation: {
                      width: qrCodeSize,
                      height: qrCodeSize
                    }
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 }
              })
            )
          } catch (error) {
            console.error(`Failed to add online QR for ${meeting.title}:`, error)
            allSections.push(
              new Paragraph({
                text: seriesTexts.qrOnlineMissing,
                alignment: AlignmentType.CENTER,
                spacing: { after: 400 }
              })
            )
          }
        }

        // 添加 Onsite QR Code
        if ((qrType === 'both' || qrType === 'onsite') && meeting.qrCodeOffline) {
          try {
            console.log(`Fetching onsite QR code: ${meeting.qrCodeOffline}`)
            const response = await fetch(meeting.qrCodeOffline)
            
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            
            const arrayBuffer = await response.arrayBuffer()
            const uint8Array = new Uint8Array(arrayBuffer)
            
            console.log(`Successfully loaded onsite QR code, size: ${uint8Array.length} bytes`)
            
            allSections.push(
              new Paragraph({
                text: seriesTexts.qrOnsiteLabel,
                heading: HeadingLevel.HEADING_2,
                alignment: AlignmentType.CENTER,
                spacing: { before: 400, after: 200 }
              })
            )

            allSections.push(
              new Paragraph({
                children: [
                  new ImageRun({
                    type: "png",
                    data: uint8Array,
                    transformation: {
                      width: qrCodeSize,
                      height: qrCodeSize
                    }
                  })
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 }
              })
            )
          } catch (error) {
            console.error(`Failed to add onsite QR for ${meeting.title}:`, error)
            allSections.push(
              new Paragraph({
                text: seriesTexts.qrOnsiteMissing,
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 }
              })
            )
          }
        }
      }

      // 生成单个Word文档
      const doc = new Document({
        sections: [{
          properties: {},
          children: allSections
        }]
      })

      const blob = await Packer.toBlob(doc)
      const typeSuffix = qrType === 'both' ? 'All' : qrType === 'online' ? 'Online' : 'Onsite'
      const fileName = `${seriesItem.title.replace(/[^a-z0-9]/gi, '_')}-${typeSuffix}-QR-Codes.docx`
      saveAs(blob, fileName)

      toast.success(seriesTexts.wordGenerateSuccess)
    } catch (error) {
      console.error("Failed to generate Word document:", error)
      toast.error(seriesTexts.wordGenerateFailed)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-600">{commonTexts.loading}</div>
      </div>
    )
  }

  const visibleSeries = [...series].sort((a, b) => {
    const ta = a.startDate ? new Date(a.startDate).getTime() : 0
    const tb = b.startDate ? new Date(b.startDate).getTime() : 0
    return ta - tb
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{seriesTexts.title}</h1>
          <p className="text-gray-600">{seriesTexts.subtitle}</p>
        </div>
        {canManage && (
          <Button onClick={() => setShowCreateForm(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {seriesTexts.createAction}
          </Button>
        )}
      </div>

      {/* Create Meeting Series Form */}
      {showCreateForm && (
        <Card>
          <CardHeader>
            <CardTitle>{seriesTexts.createFormTitle}</CardTitle>
            <CardDescription>
              {seriesTexts.createFormDescription}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreateSeries} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="title">{seriesTexts.formTitleLabel}</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder={seriesTexts.formTitlePlaceholder}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="location">{seriesTexts.formLocationLabel}</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder={seriesTexts.formLocationPlaceholder}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="city">{seriesTexts.formCityLabel}</Label>
                  <CityAutocompleteInput
                    id="city"
                    value={formData.city || ''}
                    onChange={(v) => setFormData({ ...formData, city: v })}
                    placeholder={seriesTexts.formCityPlaceholder}
                  />
                  <p className="text-xs text-gray-500 mt-1">{seriesTexts.formCityHint}</p>
                </div>
                <div>
                  <Label htmlFor="enforceCheckinMode">{t.meetingAttendance.enforceCheckinMode.label}</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Switch
                      id="enforceCheckinMode"
                      checked={formData.enforceCheckinMode}
                      disabled={!formData.enforceCheckinMode && !formData.city?.trim()}
                      onCheckedChange={(next) => setFormData({ ...formData, enforceCheckinMode: next })}
                    />
                    <span className={`text-sm ${formData.enforceCheckinMode ? 'text-emerald-600' : 'text-gray-500'}`}>
                      {formData.enforceCheckinMode
                        ? t.meetingAttendance.enforceCheckinMode.on
                        : t.meetingAttendance.enforceCheckinMode.off}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{seriesTexts.enforceCheckinModeHint}</p>
                </div>
              </div>

              <div>
                <Label htmlFor="description">{seriesTexts.formDescriptionLabel}</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={seriesTexts.formDescriptionPlaceholder}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label htmlFor="pattern">{seriesTexts.formPatternLabel}</Label>
                  <select
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.pattern}
                    onChange={(e) => setFormData({...formData, pattern: e.target.value})}
                    aria-label={seriesTexts.formPatternLabel}
                  >
                    <option value="DAILY">{seriesTexts.patternDailyOption}</option>
                    <option value="WEEKLY">{seriesTexts.patternWeeklyOption}</option>
                    <option value="MONTHLY">{seriesTexts.patternMonthlyOption}</option>
                    <option value="YEARLY">{seriesTexts.patternYearlyOption}</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="frequency">{seriesTexts.formFrequencyLabel}</Label>
                  <Input
                    id="frequency"
                    type="number"
                    value={formData.frequency}
                    onChange={(e) => setFormData({...formData, frequency: parseInt(e.target.value) || 1})}
                    min="1"
                    max="12"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="duration">{seriesTexts.formDurationLabel}</Label>
                  <Input
                    id="duration"
                    type="number"
                    value={formData.duration}
                    onChange={(e) => setFormData({...formData, duration: parseInt(e.target.value) || 60})}
                    min="15"
                    step="15"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="maxOccurrences">{seriesTexts.formMaxOccurrencesLabel}</Label>
                  <Input
                    id="maxOccurrences"
                    type="number"
                    value={formData.maxOccurrences || ''}
                    onChange={(e) => setFormData({...formData, maxOccurrences: Number(e.target.value) || 0})}
                    min="0"
                    max="100"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="timezone">
                  {seriesTexts.formTimezoneLabel}
                </Label>
                <select
                  id="timezone"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.timezone}
                  onChange={(e) => {
                    const newTimezone = e.target.value
                    const newFormData = {...formData, timezone: newTimezone}
                    
                    // 当时区改变时，转换已输入的日期时间到新时区
                    if (formData.startDateTime) {
                      const startDate = new Date(formData.startDateTime)
                      newFormData.startDateTime = convertFromUTC(startDate, newTimezone)
                    }
                    
                    setFormData(newFormData)
                  }}
                  aria-label={seriesTexts.formTimezoneLabel}
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDateTime">{seriesTexts.formStartDateTimeLabel}</Label>
                  <Input
                    id="startDateTime"
                    type="datetime-local"
                    value={formData.startDateTime}
                    onChange={(e) => setFormData({...formData, startDateTime: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">{seriesTexts.formEndDateLabel}</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="type">{seriesTexts.formTypeLabel}</Label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  aria-label={seriesTexts.formTypeLabel}
                >
                  <option value="OFFLINE">{typeTexts.offline}</option>
                  <option value="ONLINE">{typeTexts.online}</option>
                  <option value="HYBRID">{typeTexts.hybrid}</option>
                </select>
              </div>

              <div>
                <Label>{seriesTexts.formAttendeesLabel}</Label>
                <div className="space-y-3">
                  {/* 已添加的参会人列表 */}
                  {selectedAttendeesWithRoles.length > 0 && (
                    <div className="border rounded-md p-3 space-y-2">
                      {selectedAttendeesWithRoles.map((item, index) => (
                        <div key={index} className="flex items-center justify-between bg-muted p-2 rounded">
                          <div className="flex-1">
                            <div className="font-medium">{item.user.displayName}</div>
                            <div className="text-sm text-muted-foreground">{item.user.email}</div>
                          </div>
                          <div className="flex items-center gap-2">
                            <select
                              value={item.role}
                              onChange={(e) => {
                                const newList = [...selectedAttendeesWithRoles]
                                newList[index].role = e.target.value
                                setSelectedAttendeesWithRoles(newList)
                              }}
                              className="px-2 py-1 text-sm border rounded"
                            >
                              {attendeeRoleOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedAttendeesWithRoles(
                                  selectedAttendeesWithRoles.filter((_, i) => i !== index)
                                )
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* 添加参会人按钮 */}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateAttendeeModal(true)}
                    className="w-full"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {seriesTexts.formAddAttendeesAction}
                  </Button>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCreateForm(false)}
                >
                  {seriesTexts.formCancelAction}
                </Button>
                <Button type="submit">
                  {seriesTexts.formSubmitAction}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Meeting Series List */}
      <div className="space-y-4">
        {visibleSeries.map((seriesItem) => (
          <Card key={seriesItem.id} className="hover:shadow-md transition-shadow">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle className="flex items-center space-x-2">
                    <Repeat className="h-5 w-5" />
                    <span>{seriesItem.title}</span>
                  </CardTitle>
                  {seriesItem.description && (
                    <CardDescription className="mt-1 line-clamp-1">
                      {seriesItem.description}
                    </CardDescription>
                  )}
                </div>
                {canManage && (
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => toggleSeriesStatus(seriesItem.id, seriesItem.isActive)}
                    >
                      {seriesItem.isActive ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => startEditSeries(seriesItem)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => manageAttendees(seriesItem.id)}
                      className="text-green-600"
                    >
                      <Users className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => viewHistory(seriesItem.id)}
                      className="text-blue-600"
                    >
                      <History className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openUpdateScheduleModal(
                        seriesItem.id, 
                        seriesItem.meetings[0]?.startTime || new Date().toISOString(),
                        seriesItem.timezone
                      )}
                      className="text-orange-600"
                      title={seriesTexts.scheduleUpdateHint}
                    >
                      <Clock className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openDateRangeModal(seriesItem.id, 'zip')}
                      className="text-purple-600"
                      title={seriesTexts.qrDownloadZipHint}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openDateRangeModal(seriesItem.id, 'word')}
                      className="text-indigo-600"
                      title={seriesTexts.qrDownloadWordHint}
                    >
                      <FileText className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => deleteSeries(seriesItem.id)}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center text-gray-600">
                  <Repeat className="mr-2 h-4 w-4" />
                  {getPatternText(seriesItem.pattern, seriesItem.frequency)}
                </div>
                <div className="flex items-center text-gray-600">
                  <Clock className="mr-2 h-4 w-4" />
                  {seriesItem.maxOccurrences && seriesItem.maxOccurrences > 0
                    ? seriesTexts.recurrenceMaxOccurrences.replace('{count}', String(seriesItem.maxOccurrences))
                    : seriesItem.endDate
                      ? seriesTexts.recurrenceEndDate.replace('{date}', formatSeriesInstant(new Date(seriesItem.endDate), seriesItem.timezone || TENANT_DEFAULT_TIMEZONE))
                      : seriesTexts.recurrenceUnlimited}
                </div>
                {seriesItem.location && (
                  <div className="flex items-center text-gray-600">
                    <MapPin className="mr-2 h-4 w-4" />
                    {seriesItem.location}
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Users className="mr-2 h-4 w-4" />
                  {seriesTexts.instancesCount.replace('{count}', String(seriesItem.meetings.length))}
                </div>
              </div>

              <div className="text-xs text-gray-500">
                {seriesTexts.createdByLabel.replace('{name}', seriesItem.creator.displayName)}
              </div>

              {/* Meeting Instances List */}
              {seriesItem.meetings.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium mb-3">{seriesTexts.instancesTitle}</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {(expandedSeries.has(seriesItem.id) 
                      ? seriesItem.meetings 
                      : seriesItem.meetings.slice(0, 6)
                    ).map((meeting) => (
                      <div
                        key={meeting.id}
                        className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                        onClick={() => router.push(`/meetingattendance/meetings/${meeting.id}`)}
                      >
                        <div className="text-sm font-medium">{meeting.title}</div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatSeriesInstant(new Date(meeting.startTime), seriesItem.timezone || "UTC")}
                        </div>
                        <div className={`text-xs mt-1 ${getStatusColor(meeting.status)}`}>
                          {meeting.status === 'SCHEDULED' && statusTexts.scheduled}
                          {meeting.status === 'IN_PROGRESS' && statusTexts.inProgress}
                          {meeting.status === 'COMPLETED' && statusTexts.completed}
                          {meeting.status === 'CANCELLED' && statusTexts.cancelled}
                        </div>
                      </div>
                    ))}
                  </div>
                  {seriesItem.meetings.length > 6 && (
                    <div className="text-center mt-3">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setExpandedSeries(prev => {
                            const newSet = new Set(prev)
                            if (newSet.has(seriesItem.id)) {
                              newSet.delete(seriesItem.id)
                            } else {
                              newSet.add(seriesItem.id)
                            }
                            return newSet
                          })
                        }}
                      >
                        {expandedSeries.has(seriesItem.id) 
                          ? seriesTexts.instancesShowLess
                          : seriesTexts.instancesShowMore.replace('{count}', String(seriesItem.meetings.length - 6))
                        }
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {visibleSeries.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <Repeat className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{seriesTexts.emptyTitle}</h3>
            <p className="text-gray-600 mb-4">{seriesTexts.emptyDescription}</p>
            {canManage && (
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {seriesTexts.createAction}
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Edit Meeting Series Form */}
      {showEditForm && editingSeries && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{seriesTexts.editFormTitle}</CardTitle>
                    <CardDescription>{seriesTexts.editFormDescription}</CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowEditForm(false)
                      setEditingSeries(null)
                      resetForm()
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleEditSeries} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-title">{seriesTexts.formTitleLabel}</Label>
                      <Input
                        id="edit-title"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-location">{seriesTexts.formLocationLabel}</Label>
                      <Input
                        id="edit-location"
                        value={formData.location}
                        onChange={(e) => setFormData({...formData, location: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="edit-city">{seriesTexts.formCityLabel}</Label>
                      <CityAutocompleteInput
                        id="edit-city"
                        value={formData.city || ''}
                        onChange={(v) => setFormData({ ...formData, city: v })}
                        placeholder={seriesTexts.formCityPlaceholder}
                      />
                      <p className="text-xs text-gray-500 mt-1">{seriesTexts.formCityHint}</p>
                    </div>
                    <div>
                      <Label htmlFor="edit-enforce">{t.meetingAttendance.enforceCheckinMode.label}</Label>
                      <div className="flex items-center gap-3 mt-2">
                        <Switch
                          id="edit-enforce"
                          checked={formData.enforceCheckinMode}
                          disabled={!formData.enforceCheckinMode && !formData.city?.trim()}
                          onCheckedChange={(next) => setFormData({ ...formData, enforceCheckinMode: next })}
                        />
                        <span className={`text-sm ${formData.enforceCheckinMode ? 'text-emerald-600' : 'text-gray-500'}`}>
                          {formData.enforceCheckinMode
                            ? t.meetingAttendance.enforceCheckinMode.on
                            : t.meetingAttendance.enforceCheckinMode.off}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{seriesTexts.enforceCheckinModeHint}</p>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="edit-description">{seriesTexts.formDescriptionLabel}</Label>
                    <Input
                      id="edit-description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label htmlFor="edit-pattern">{seriesTexts.formPatternLabel}</Label>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        value={formData.pattern}
                        onChange={(e) => setFormData({...formData, pattern: e.target.value})}
                        aria-label={seriesTexts.formPatternLabel}
                      >
                        <option value="DAILY">{seriesTexts.patternDailyOption}</option>
                        <option value="WEEKLY">{seriesTexts.patternWeeklyOption}</option>
                        <option value="MONTHLY">{seriesTexts.patternMonthlyOption}</option>
                        <option value="YEARLY">{seriesTexts.patternYearlyOption}</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="edit-frequency">{seriesTexts.formFrequencyLabel}</Label>
                      <Input
                        id="edit-frequency"
                        type="number"
                        value={formData.frequency}
                        onChange={(e) => setFormData({...formData, frequency: parseInt(e.target.value) || 1})}
                        min="1"
                        max="12"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-maxOccurrences">{seriesTexts.formMaxOccurrencesLabel}</Label>
                      <Input
                        id="edit-maxOccurrences"
                        type="number"
                        value={formData.maxOccurrences || ''}
                        onChange={(e) => setFormData({...formData, maxOccurrences: Number(e.target.value) || 0})}
                        min="0"
                        max="100"
                      />
                    </div>
                    <div>
                      <Label htmlFor="edit-endDate">{seriesTexts.formEndDateLabel}</Label>
                      <Input
                        id="edit-endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) => setFormData({...formData, endDate: e.target.value})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="edit-type">{seriesTexts.formTypeLabel}</Label>
                    <select
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.type}
                      onChange={(e) => setFormData({...formData, type: e.target.value})}
                      aria-label={seriesTexts.formTypeLabel}
                    >
                      <option value="OFFLINE">{typeTexts.offline}</option>
                      <option value="ONLINE">{typeTexts.online}</option>
                      <option value="HYBRID">{typeTexts.hybrid}</option>
                    </select>
                  </div>

                  <div>
                    <Label htmlFor="edit-timezone">
                      {seriesTexts.formTimezoneLabel}
                    </Label>
                    <select
                      id="edit-timezone"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.timezone}
                      onChange={(e) => {
                        const newTimezone = e.target.value
                        const newFormData = {...formData, timezone: newTimezone}
                        
                        // 当时区改变时，转换已输入的日期时间到新时区
                        if (formData.startDateTime) {
                          const startDate = new Date(formData.startDateTime)
                          newFormData.startDateTime = convertFromUTC(startDate, newTimezone)
                        }
                        
                        setFormData(newFormData)
                      }}
                      aria-label={seriesTexts.formTimezoneLabel}
                    >
                      {COMMON_TIMEZONES.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowEditForm(false)
                        setEditingSeries(null)
                        resetForm()
                      }}
                    >
                      {seriesTexts.formCancelAction}
                    </Button>
                    <Button type="submit">
                      {seriesTexts.editFormSubmitAction}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Meeting History Modal */}
      {showHistoryModal && historyData && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>{seriesTexts.historyTitle}</CardTitle>
                    <CardDescription>
                      {seriesTexts.historySummary
                        .replace('{totalMeetings}', String(historyData?.summary?.totalMeetings || 0))
                        .replace('{averageAttendance}', String(historyData?.summary?.averageAttendance || 0))}
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowHistoryModal(false)
                      setHistoryData(null)
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {!historyData?.meetings || historyData.meetings.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {seriesTexts.historyEmpty}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {historyData?.meetings?.map((meeting: HistoryMeeting) => (
                      <div key={meeting.id} className="border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-medium">{meeting.title}</h4>
                            <div className="text-sm text-gray-600 space-y-0.5">
                              <div>{commonTexts.start}: {formatSeriesInstant(new Date(meeting.startTime))}</div>
                              <div>{commonTexts.end}: {formatSeriesInstant(new Date(meeting.endTime))}</div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-lg font-semibold text-green-600">
                              {meeting.stats.attendanceRate}%
                            </div>
                            <div className="text-xs text-gray-500">{seriesTexts.historyAttendanceRate}</div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div className="flex items-center text-gray-600">
                            <Users className="mr-2 h-4 w-4" />
                            {seriesTexts.historyRequired.replace('{count}', String(meeting.stats.totalRequired))}
                          </div>
                          <div className="flex items-center text-green-600">
                            <Users className="mr-2 h-4 w-4" />
                            {seriesTexts.historyAttended.replace('{count}', String(meeting.stats.actualAttendees))}
                          </div>
                          <div className="flex items-center text-red-600">
                            <Users className="mr-2 h-4 w-4" />
                            {seriesTexts.historyAbsent.replace('{count}', String(meeting.stats.absentees))}
                          </div>
                        </div>

                        {meeting.attendances.length > 0 && (
                          <div className="mt-3 pt-3 border-t">
                            <div className="text-xs text-gray-500 mb-2">{seriesTexts.historyAttendeesLabel}</div>
                            <div className="flex flex-wrap gap-1">
                              {meeting.attendances
                                .filter((a) => a.status === "ON_SITE" || a.status === "ONLINE")
                                .map((attendance) => (
                                <span
                                  key={attendance.id}
                                  className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs"
                                >
                                  {attendance.user.displayName}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Attendees Management Modal */}
      {showAttendeesModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-6 w-full max-w-5xl max-h-[90vh] overflow-y-auto">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{seriesTexts.attendeeManageTitle}</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowAttendeesModal(false)
                      setDefaultAttendees([])
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  {seriesTexts.attendeeManageDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Default Attendees List */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium">
                    {seriesTexts.attendeeManageCurrent.replace('{count}', String(defaultAttendees.length))}
                  </h4>
                  <Button
                    onClick={() => setShowAddAttendeeModal(true)}
                    size="sm"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {seriesTexts.attendeeAddAction}
                  </Button>
                </div>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <Input
                      value={attendeePrefKeyword}
                      onChange={(e) => setAttendeePrefKeyword(e.target.value)}
                      placeholder={seriesTexts.attendeeSearchPlaceholder}
                      className="w-64 h-8 text-sm"
                    />
                    <span className="text-xs text-gray-500">
                      {seriesTexts.attendeeBulkSelectedCount.replace('{count}', String(selectedForBulk.size))}
                    </span>
                    <div className="ml-auto flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedForBulk.size === 0}
                        onClick={() => handleBulkSetAttendeePref('ON_SITE')}
                      >
                        {seriesTexts.attendeeBulkSetOnSite}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedForBulk.size === 0}
                        onClick={() => handleBulkSetAttendeePref('ONLINE')}
                      >
                        {seriesTexts.attendeeBulkSetOnline}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={selectedForBulk.size === 0}
                        onClick={() => handleBulkSetAttendeePref(null)}
                      >
                        {seriesTexts.attendeeBulkSetFollowWorkCity}
                      </Button>
                    </div>
                  </div>

                  {defaultAttendees.length > 0 ? (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                     {defaultAttendees
                        .filter((a) => {
                          const kw = attendeePrefKeyword.trim().toLowerCase()
                          if (!kw) return true
                          const name = (a.user?.displayName || a.displayName || '').toLowerCase()
                          const email = (a.user?.email || a.email || '').toLowerCase()
                          return name.includes(kw) || email.includes(kw)
                        })
                        .sort((a, b) => {
                          // Sort by name initial
                          const nameA = (a.displayName || a.user?.displayName || '').toLowerCase()
                          const nameB = (b.displayName || b.user?.displayName || '').toLowerCase()
                          return nameA.localeCompare(nameB, 'en-US', { sensitivity: 'base' })
                        })
                        .map((attendee, index) => {
                          const uid = attendee.user?.id
                          const pref = uid ? attendeePrefs[uid] ?? null : null
                          const selected = uid ? selectedForBulk.has(uid) : false
                          return (
                            <div key={attendee.id || index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                              {uid && (
                                <input
                                  type="checkbox"
                                  className="mr-3 h-4 w-4"
                                  checked={selected}
                                  onChange={(e) => {
                                    setSelectedForBulk((prev) => {
                                      const next = new Set(prev)
                                      if (e.target.checked) next.add(uid)
                                      else next.delete(uid)
                                      return next
                                    })
                                  }}
                                />
                              )}
                              <div className="flex-1">
                                <div className="font-medium">{attendee.user?.displayName || attendee.displayName}</div>
                                <div className="text-sm text-gray-500">{attendee.user?.email || attendee.email}</div>
                                {attendee.user?.department?.name && (
                                  <div className="text-xs text-gray-400">
                                    {seriesTexts.attendeeDepartmentLabel.replace('{value}', attendee.user.department.name)}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center space-x-2">
                                <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                  {getAttendeeRoleLabel(attendee.role)}
                                </span>
                                {uid && (
                                  <select
                                    className="h-8 text-xs rounded border border-gray-300 px-2"
                                    value={pref ?? 'FOLLOW'}
                                    onChange={(e) => {
                                      const v = e.target.value
                                      handleChangeAttendeePref(uid, v === 'FOLLOW' ? null : (v as CheckinMode))
                                    }}
                                    title={seriesTexts.attendeePrefLabel}
                                  >
                                    <option value="FOLLOW">{seriesTexts.attendeePrefFollow}</option>
                                    <option value="ON_SITE">{seriesTexts.attendeePrefOnSite}</option>
                                    <option value="ONLINE">{seriesTexts.attendeePrefOnline}</option>
                                  </select>
                                )}
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => attendee.id && openEditRoleModal(attendee)}
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Edit role for current and future meetings"
                                >
                                  <UserCog className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => attendee.id && removeAttendee(attendee.id)}
                                  className="text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          )
                        })}
                    </div>
                  ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Users className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                            <p>{seriesTexts.attendeeNoneTitle}</p>
                            <p className="text-sm">{seriesTexts.attendeeNoneDescription}</p>
                          </div>
                        )}
                </div>

                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAttendeesModal(false)
                      setDefaultAttendees([])
                    }}
                  >
                    {seriesTexts.closeAction}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Add Attendee Modal (for existing series) */}
      {showAddAttendeeModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-6 w-full max-w-2xl">
            <Card>
              <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{seriesTexts.attendeeAddModalTitle}</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                    onClick={() => {
                      setShowAddAttendeeModal(false)
                      setNewAttendeeUsers([])
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  {seriesTexts.attendeeAddModalDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="newRole">{attendeeTexts.roleLabel}</Label>
                  <select
                    id="newRole"
                    value={newAttendeeRole}
                    onChange={(e) => setNewAttendeeRole(e.target.value)}
                    title={attendeeTexts.roleLabel}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {attendeeRoleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <MeetingUserSelector
                  selectedUsers={newAttendeeUsers}
                  onUsersChange={setNewAttendeeUsers}
                />

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowAddAttendeeModal(false)
                      setNewAttendeeUsers([])
                    }}
                  >
                    {attendeeTexts.cancel}
                  </Button>
                  <Button 
                    onClick={addAttendees} 
                    disabled={newAttendeeUsers.length === 0}
                  >
                    {seriesTexts.attendeeAddAction}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Edit Attendee Role Modal */}
      {showEditRoleModal && editingAttendee && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg mx-4 shadow-2xl border-0 animate-in fade-in zoom-in duration-200">
            <CardHeader className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                    <UserCog className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{seriesTexts.attendeeRoleModalTitle}</CardTitle>
                    <CardDescription className="text-blue-50 text-sm mt-1">
                      {seriesTexts.attendeeRoleModalDescription}
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowEditRoleModal(false)
                    setEditingAttendee(null)
                  }}
                  className="hover:bg-white/20 text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6 bg-gray-50">
              {/* Info Banner */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-amber-900 space-y-1">
                    <p className="font-medium">{seriesTexts.attendeeRoleNoticeTitle}</p>
                    <ul className="list-disc list-inside space-y-1 text-amber-800 ml-2">
                      <li>{seriesTexts.attendeeRoleNoticeItemCurrent}</li>
                      <li>{seriesTexts.attendeeRoleNoticeItemPast}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Attendee Info */}
              <div className="bg-white rounded-lg p-4 border border-gray-200">
                <Label className="text-sm font-semibold text-gray-700 mb-2 block">{seriesTexts.attendeeRoleAttendeeLabel}</Label>
                <div>
                  <div className="font-medium text-gray-900">{editingAttendee.displayName}</div>
                  <div className="text-sm text-gray-500">{editingAttendee.email}</div>
                  <div className="mt-2 inline-block px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                    {seriesTexts.attendeeRoleCurrent.replace('{role}', getAttendeeRoleLabel(editingAttendee.role))}
                  </div>
                </div>
              </div>

              {/* New Role Selection */}
              <div className="space-y-2">
                <Label htmlFor="editRole" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <UserCog className="h-4 w-4 text-blue-600" />
                  {seriesTexts.attendeeRoleNewLabel}
                </Label>
                <select
                  id="editRole"
                  value={newEditRole}
                  onChange={(e) => setNewEditRole(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                  required
                >
                  {attendeeRoleOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  {seriesTexts.attendeeRoleNewHint}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditRoleModal(false)
                    setEditingAttendee(null)
                  }}
                  className="flex-1 border-gray-300 hover:bg-gray-100"
                >
                  {attendeeTexts.cancel}
                </Button>
                <Button
                  onClick={updateAttendeeRole}
                  disabled={!newEditRole || newEditRole === editingAttendee.role}
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <UserCog className="h-4 w-4 mr-2" />
                  {seriesTexts.attendeeRoleUpdateAction}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Attendee Modal (for create form) */}
      {showCreateAttendeeModal && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white/95 backdrop-blur-md rounded-lg shadow-xl border border-white/20 p-6 w-full max-w-2xl">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>{seriesTexts.createAttendeeModalTitle}</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCreateAttendeeModal(false)
                      setCreateFormAttendeeUsers([])
                      setCreateFormAttendeeRole("OPTIONAL_ATTENDEE")
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <CardDescription>
                  {seriesTexts.createAttendeeModalDescription}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="createFormRole">{attendeeTexts.roleLabel}</Label>
                  <select
                    id="createFormRole"
                    value={createFormAttendeeRole}
                    onChange={(e) => setCreateFormAttendeeRole(e.target.value)}
                    title={attendeeTexts.roleLabel}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {attendeeRoleOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <MeetingUserSelector
                  selectedUsers={createFormAttendeeUsers}
                  onUsersChange={setCreateFormAttendeeUsers}
                />

                <div className="flex justify-end space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowCreateAttendeeModal(false)
                      setCreateFormAttendeeUsers([])
                      setCreateFormAttendeeRole("OPTIONAL_ATTENDEE")
                    }}
                  >
                    {attendeeTexts.cancel}
                  </Button>
                  <Button 
                    onClick={() => {
                      // 添加选中的用户到创建表单的参会人列表
                      const newAttendees = createFormAttendeeUsers.map(user => ({
                        user,
                        role: createFormAttendeeRole
                      }))
                      // 过滤掉已存在的用户
                      const filtered = newAttendees.filter(
                        newItem => !selectedAttendeesWithRoles.some(
                          existing => existing.user.id === newItem.user.id
                        )
                      )
                      setSelectedAttendeesWithRoles([...selectedAttendeesWithRoles, ...filtered])
                      // 不关闭弹窗，让用户可以继续添加
                      // setShowCreateAttendeeModal(false)
                      setCreateFormAttendeeUsers([])
                      setCreateFormAttendeeRole("OPTIONAL_ATTENDEE")
                    }}
                    disabled={createFormAttendeeUsers.length === 0}
                  >
                    {seriesTexts.formAddAttendeesAction}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Update Schedule Modal */}
      {showUpdateScheduleModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg mx-4 shadow-2xl border-0 animate-in fade-in zoom-in duration-200">
            <CardHeader className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">{seriesTexts.scheduleModalTitle}</CardTitle>
                    <CardDescription className="text-orange-50 text-sm mt-1">
                      {seriesTexts.scheduleModalDescription}
                    </CardDescription>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowUpdateScheduleModal(false)}
                  className="hover:bg-white/20 text-white"
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6 bg-gray-50">
              {/* Info Banner */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-900 space-y-1">
                    <p className="font-medium">{seriesTexts.scheduleNoticeTitle}</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800 ml-2">
                      <li>{seriesTexts.scheduleNoticeItemCurrent}</li>
                      <li>{seriesTexts.scheduleNoticeItemPast}</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Start Time */}
              <div className="space-y-2">
                <Label htmlFor="startTime" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  {seriesTexts.scheduleStartTimeLabel}
                </Label>
                <Input
                  id="startTime"
                  type="time"
                  value={scheduleUpdate.startTime}
                  onChange={(e) => setScheduleUpdate({ ...scheduleUpdate, startTime: e.target.value })}
                  className="bg-white border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all shadow-sm"
                  required
                />
                <p className="text-xs text-gray-500">
                  {seriesTexts.scheduleStartTimeHint}
                </p>
              </div>

              {/* Duration */}
              <div className="space-y-2">
                <Label htmlFor="duration" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  {seriesTexts.scheduleDurationLabel}
                </Label>
                <Input
                  id="duration"
                  type="number"
                  min="1"
                  value={scheduleUpdate.duration}
                  onChange={(e) => setScheduleUpdate({ ...scheduleUpdate, duration: parseInt(e.target.value) || 60 })}
                  className="bg-white border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all shadow-sm"
                  required
                />
                <p className="text-xs text-gray-500">
                  {seriesTexts.scheduleDurationHint}
                </p>
              </div>

              {/* Timezone */}
              <div className="space-y-2">
                <Label htmlFor="timezone" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-orange-600" />
                  {seriesTexts.scheduleTimezoneLabel}
                </Label>
                <select
                  id="timezone"
                  value={scheduleUpdate.timezone}
                  onChange={(e) => setScheduleUpdate({ ...scheduleUpdate, timezone: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-all shadow-sm"
                  required
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500">
                  {seriesTexts.scheduleTimezoneHint}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  variant="outline"
                  onClick={() => setShowUpdateScheduleModal(false)}
                  className="flex-1 border-gray-300 hover:bg-gray-100"
                >
                  {attendeeTexts.cancel}
                </Button>
                <Button
                  onClick={updateSeriesSchedule}
                  className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white shadow-lg"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  {seriesTexts.scheduleUpdateAction}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Date Range Selection Modal */}
      {showDateRangeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg mx-4 shadow-2xl border-0 animate-in fade-in zoom-in duration-200">
            <CardHeader className="bg-white border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600 rounded-lg">
                  <Calendar className="h-5 w-5 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl text-gray-900">{seriesTexts.rangeTitle}</CardTitle>
                  <CardDescription className="text-sm mt-1 text-gray-600">
                    {seriesTexts.rangeDescription}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6 pt-6 bg-gray-50">
              {/* Date Inputs */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    {seriesTexts.rangeStartLabel}
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                    className="bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    {seriesTexts.rangeEndLabel}
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                    className="bg-white border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all shadow-sm"
                  />
                </div>
              </div>

              {/* QR Code Type Selection */}
              <div className="space-y-3">
                <Label className="text-sm font-semibold text-gray-700">{seriesTexts.qrTypeLabel}</Label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setQrCodeType('both')}
                    className={`px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                      qrCodeType === 'both'
                        ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    {seriesTexts.qrTypeBothOption}
                  </button>
                  <button
                    type="button"
                    onClick={() => setQrCodeType('online')}
                    className={`px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                      qrCodeType === 'online'
                        ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    {seriesTexts.qrTypeOnlineOption}
                  </button>
                  <button
                    type="button"
                    onClick={() => setQrCodeType('onsite')}
                    className={`px-4 py-2.5 rounded-lg border-2 font-medium text-sm transition-all ${
                      qrCodeType === 'onsite'
                        ? 'bg-blue-50 border-blue-500 text-blue-700 shadow-sm'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300 hover:bg-blue-50/50'
                    }`}
                  >
                    {seriesTexts.qrTypeOnsiteOption}
                  </button>
                </div>
              </div>

              {/* Meeting Count Info */}
              {selectedSeriesId && (() => {
                const seriesItem = series.find(s => s.id === selectedSeriesId)
                if (!seriesItem) return null
                
                const filteredCount = seriesItem.meetings.filter(meeting => {
                  const meetingDate = new Date(meeting.startTime)
                  const start = dateRange.startDate ? new Date(dateRange.startDate) : null
                  const end = dateRange.endDate ? new Date(dateRange.endDate) : null
                  
                  if (start && end) {
                    return meetingDate >= start && meetingDate <= end
                  } else if (start) {
                    return meetingDate >= start
                  } else if (end) {
                    return meetingDate <= end
                  }
                  return true
                }).length
                
                return (
                  <div className={`flex items-center gap-3 p-4 rounded-lg border-2 transition-all ${
                    filteredCount > 0 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-amber-50 border-amber-200'
                  }`}>
                    <div className={`p-2 rounded-full ${
                      filteredCount > 0 ? 'bg-blue-600' : 'bg-amber-500'
                    }`}>
                      <Info className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        filteredCount > 0 ? 'text-blue-900' : 'text-amber-900'
                      }`}>
                        {filteredCount > 0 
                          ? seriesTexts.rangeCountIncluded.replace('{count}', String(filteredCount))
                          : seriesTexts.rangeCountEmpty
                        }
                      </p>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {seriesTexts.rangeTotalLabel.replace('{count}', String(seriesItem.meetings.length))}
                      </p>
                    </div>
                  </div>
                )
              })()}

              {/* Action Buttons */}
              <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 -mx-6 px-6 -mb-6 pb-6 bg-white">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDateRangeModal(false)
                    setSelectedSeriesId(null)
                    setDateRangeAction(null)
                    setDateRange({ startDate: "", endDate: "" })
                  }}
                  className="min-w-[100px]"
                >
                  {attendeeTexts.cancel}
                </Button>
                <Button 
                  onClick={confirmDownload}
                  className="min-w-[140px]"
                  disabled={(() => {
                    if (!selectedSeriesId) return true
                    
                    const seriesItem = series.find(s => s.id === selectedSeriesId)
                    if (!seriesItem) return true
                    
                    const filteredCount = seriesItem.meetings.filter(meeting => {
                      const meetingDate = new Date(meeting.startTime)
                      const start = dateRange.startDate ? new Date(dateRange.startDate) : null
                      const end = dateRange.endDate ? new Date(dateRange.endDate) : null
                      
                      if (start && end) {
                        return meetingDate >= start && meetingDate <= end
                      } else if (start) {
                        return meetingDate >= start
                      } else if (end) {
                        return meetingDate <= end
                      }
                      return true
                    }).length
                    
                    return filteredCount === 0
                  })()}
                >
                  {dateRangeAction === 'zip' ? (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      {seriesTexts.rangeDownloadZip}
                    </>
                  ) : (
                    <>
                      <FileText className="h-4 w-4 mr-2" />
                      {seriesTexts.rangeDownloadWord}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  )
}
